import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Paper
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { slideScripts } from '../utils/slideScripts';

interface ScriptingSidebarProps {
  open: boolean;
  onClose: () => void;
}

const ScriptingSidebar: React.FC<ScriptingSidebarProps> = ({ open, onClose }) => {
  const currentSlide = useSelector((state: RootState) => state.presentation.currentSlide);
  const presentationState = useSelector((state: RootState) => state.presentation);

  const getCurrentScript = () => {
    switch (currentSlide) {
      case 0:
        return slideScripts.introduction;
      case 1:
        return slideScripts.creditAnalysis;
      case 2:
        return slideScripts.interestCalculator;
      case 3:
        return slideScripts.termOptions;
      case 4:
        return slideScripts.logistics;
      case 5:
        return slideScripts.summary;
      case 6:
        return slideScripts.additionalInfo;
      default:
        return slideScripts.introduction;
    }
  };

  const formatScript = (script: string) => {
    return script.replace(/\[([^\]]+)\]/g, (match, key) => {
      switch (key) {
        case 'percentage':
          return presentationState.calculations.creditUtilization?.toFixed(1) || '75.5';
        case 'total monthly payment':
          return `$${presentationState.calculations.currentMonthlyPayment?.toLocaleString() || '1,260'}`;
        case 'monthly interest':
          return `$${presentationState.calculations.monthlyInterest?.toLocaleString() || '800'}`;
        case 'annual interest':
          return `$${presentationState.calculations.annualInterest?.toLocaleString() || '9,600'}`;
        case 'current payment':
          return `$${presentationState.calculations.currentMonthlyPayment?.toLocaleString() || '1,260'}`;
        case 'proposed payment':
          return `$${presentationState.calculations.proposedMonthlyPayment?.toLocaleString() || '800'}`;
        case 'term':
          return `${presentationState.selectedTerms || 48}`;
        case 'total savings':
          return `$${presentationState.calculations.totalSavings?.toLocaleString() || '22,000'}`;
        default:
          return match;
      }
    });
  };

  const currentScript = getCurrentScript();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="persistent"
      sx={{
        width: 400,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 400,
          boxSizing: 'border-box',
          mt: '64px', // Adjust based on your app bar height
        },
      }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Presenter Notes</Typography>
        <IconButton onClick={onClose}>
          <ChevronRightIcon />
        </IconButton>
      </Box>
      
      <Divider />

      <Box sx={{ p: 2 }}>
        <Typography variant="h6" color="primary" gutterBottom>
          {currentScript.title}
        </Typography>

        <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" gutterBottom>
            Key Points:
          </Typography>
          <List dense>
            {currentScript.keyPoints.map((point, index) => (
              <ListItem key={index}>
                <ListItemText primary={`• ${point}`} />
              </ListItem>
            ))}
          </List>
        </Paper>

        <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" gutterBottom>
            Suggested Script:
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {formatScript(currentScript.script)}
          </Typography>
        </Paper>
      </Box>
    </Drawer>
  );
};

export default ScriptingSidebar;
